<template>
  <div class="syntax-guide">
    <div class="container">
      <div class="header">
        <h1>Mermaid 语法说明</h1>
        <p>详细的 Mermaid 图形语法参考和示例</p>
      </div>

      <div class="syntax-sections">
        <div class="section" v-for="section in syntaxSections" :key="section.id">
          <h2>{{ section.title }}</h2>
          <p class="description">{{ section.description }}</p>
          
          <div class="examples">
            <div v-for="example in section.examples" :key="example.title" class="example-card">
              <h3>{{ example.title }}</h3>
              <div class="code-block">
                <pre><code>{{ example.code }}</code></pre>
                <button class="copy-btn" @click="copyCode(example.code)">
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
              <div class="preview" :id="`preview-${section.id}-${example.id}`"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="quick-actions">
        <router-link to="/diagram" class="action-btn primary">
          <i class="bi bi-play-circle"></i>
          开始创建图表
        </router-link>
        <a href="https://mermaid.js.org/intro/" target="_blank" class="action-btn secondary">
          <i class="bi bi-box-arrow-up-right"></i>
          官方文档
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import mermaid from 'mermaid'

export default {
  name: 'MermaidSyntax',
  data() {
    return {
      syntaxSections: [
        {
          id: 'flowchart',
          title: '流程图 (Flowchart)',
          description: '用于描述流程、决策和步骤的图表',
          examples: [
            {
              id: 1,
              title: '基本流程图',
              code: `flowchart TD
    A[开始] --> B{判断条件}
    B -->|是| C[执行操作]
    B -->|否| D[跳过操作]
    C --> E[结束]
    D --> E`
            },
            {
              id: 2,
              title: '节点形状',
              code: `flowchart LR
    A[方形] --> B(圆角)
    B --> C{菱形}
    C --> D((圆形))
    D --> E>标签形]`
            }
          ]
        },
        {
          id: 'sequence',
          title: '时序图 (Sequence Diagram)',
          description: '展示对象之间的交互时序',
          examples: [
            {
              id: 1,
              title: '基本时序图',
              code: `sequenceDiagram
    participant A as 客户端
    participant B as 服务器
    A->>B: 发送请求
    B-->>A: 返回响应
    A->>B: 确认收到
    Note over A,B: 通信完成`
            }
          ]
        },
        {
          id: 'gantt',
          title: '甘特图 (Gantt Chart)',
          description: '项目进度和时间安排图表',
          examples: [
            {
              id: 1,
              title: '项目甘特图',
              code: `gantt
    title 项目开发计划
    dateFormat YYYY-MM-DD
    section 设计阶段
    需求分析    :a1, 2024-01-01, 30d
    UI设计      :after a1, 20d
    section 开发阶段
    前端开发    :2024-02-01, 45d
    后端开发    :2024-02-15, 30d`
            }
          ]
        },
        {
          id: 'pie',
          title: '饼图 (Pie Chart)',
          description: '显示数据占比的圆形图表',
          examples: [
            {
              id: 1,
              title: '市场份额饼图',
              code: `pie title 浏览器市场份额
    "Chrome" : 65.2
    "Safari" : 18.4
    "Firefox" : 9.1
    "Edge" : 4.8
    "其他" : 2.5`
            }
          ]
        },
        {
          id: 'class',
          title: '类图 (Class Diagram)',
          description: '面向对象设计中的类关系图',
          examples: [
            {
              id: 1,
              title: '基本类图',
              code: `classDiagram
    class Animal {
        +String name
        +int age
        +makeSound()
        +move()
    }
    class Dog {
        +String breed
        +bark()
        +wagTail()
    }
    Animal <|-- Dog`
            }
          ]
        },
        {
          id: 'state',
          title: '状态图 (State Diagram)',
          description: '描述系统状态变化的图表',
          examples: [
            {
              id: 1,
              title: '订单状态图',
              code: `stateDiagram-v2
    [*] --> 待付款
    待付款 --> 已付款 : 支付成功
    待付款 --> 已取消 : 取消订单
    已付款 --> 已发货 : 商家发货
    已发货 --> 已完成 : 确认收货
    已取消 --> [*]
    已完成 --> [*]`
            }
          ]
        },
        {
          id: 'er',
          title: 'ER图 (Entity Relationship)',
          description: '数据库实体关系图',
          examples: [
            {
              id: 1,
              title: '用户订单ER图',
              code: `erDiagram
    USER {
        int id PK
        string name
        string email
        date created_at
    }
    ORDER {
        int id PK
        int user_id FK
        decimal amount
        date order_date
    }
    PRODUCT {
        int id PK
        string name
        decimal price
    }
    USER ||--o{ ORDER : places
    ORDER }o--|| PRODUCT : contains`
            }
          ]
        },
        {
          id: 'git',
          title: 'Git图 (Git Graph)',
          description: 'Git分支和提交历史图',
          examples: [
            {
              id: 1,
              title: 'Git分支图',
              code: `gitGraph
    commit id: "初始提交"
    commit id: "添加功能A"
    branch develop
    checkout develop
    commit id: "开发功能B"
    commit id: "修复bug"
    checkout main
    merge develop
    commit id: "发布v1.0"`
            }
          ]
        },
        {
          id: 'mindmap',
          title: '思维导图 (Mindmap)',
          description: '展示思维结构和概念关系',
          examples: [
            {
              id: 1,
              title: '项目规划思维导图',
              code: `mindmap
  root((项目规划))
    需求分析
      用户调研
        问卷调查
        用户访谈
      竞品分析
        功能对比
        价格分析
    设计阶段
      UI设计
        原型设计
        视觉设计
      交互设计
        用户体验
        交互流程
    开发阶段
      前端开发
      后端开发
    测试阶段
      单元测试
      集成测试`
            }
          ]
        },
        {
          id: 'timeline',
          title: '时间线 (Timeline)',
          description: '展示事件的时间顺序和发展历程',
          examples: [
            {
              id: 1,
              title: '产品发展时间线',
              code: `timeline
    title 产品发展历程
    2021 : 项目启动
         : 团队组建
         : 需求调研
    2022 : 产品设计
         : 技术选型
         : 开发阶段
    2023 : 测试上线
         : 用户反馈
         : 问题修复
    2024 : 功能迭代
         : 性能优化
         : 市场推广`
            }
          ]
        }
      ]
    }
  },
  mounted() {
    mermaid.initialize({ 
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose'
    })
    this.$nextTick(() => {
      this.renderAllDiagrams()
    })
  },
  methods: {
    async renderAllDiagrams() {
      for (const section of this.syntaxSections) {
        for (const example of section.examples) {
          await this.renderDiagram(section.id, example.id, example.code)
        }
      }
    },
    async renderDiagram(sectionId, exampleId, code) {
      try {
        const container = document.getElementById(`preview-${sectionId}-${exampleId}`)
        if (!container) return
        
        const id = `diagram-${sectionId}-${exampleId}-${Date.now()}`
        const isValid = await mermaid.parse(code)
        
        if (isValid) {
          const { svg } = await mermaid.render(id, code)
          container.innerHTML = svg
        }
      } catch (err) {
        console.error('渲染图表失败:', err)
      }
    },
    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.$message?.success?.('代码已复制到剪贴板') || alert('代码已复制')
      })
    }
  }
}
</script>

<style scoped>
.syntax-guide {
  padding: 40px 0;
  background: #f8f9fa;
  min-height: calc(100vh - 140px);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.header {
  text-align: center;
  margin-bottom: 50px;
}

.header h1 {
  font-size: 36px;
  font-weight: 300;
  margin-bottom: 15px;
  color: #333;
}

.header p {
  color: #666;
  font-size: 16px;
}

.syntax-sections {
  margin-bottom: 60px;
}

.section {
  background: white;
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
}

.section h2 {
  color: #4a90e2;
  font-size: 24px;
  margin-bottom: 10px;
}

.description {
  color: #666;
  margin-bottom: 25px;
  font-size: 16px;
}

.examples {
  display: grid;
  gap: 25px;
}

.example-card {
  border: 1px solid #eee;
  border-radius: 10px;
  overflow: hidden;
}

.example-card h3 {
  background: #f8f9fa;
  padding: 15px 20px;
  margin: 0;
  font-size: 16px;
  color: #333;
  border-bottom: 1px solid #eee;
}

.code-block {
  position: relative;
  background: #2d3748;
  color: #e2e8f0;
}

.code-block pre {
  margin: 0;
  padding: 20px;
  overflow-x: auto;
}

.code-block code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
}

.copy-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255,255,255,0.1);
  border: none;
  color: white;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;
}

.copy-btn:hover {
  background: rgba(255,255,255,0.2);
}

.preview {
  padding: 20px;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.preview svg {
  max-width: 100%;
  height: auto;
}

.quick-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s;
}

.action-btn.primary {
  background: #4a90e2;
  color: white;
}

.action-btn.primary:hover {
  background: #357abd;
  transform: translateY(-2px);
}

.action-btn.secondary {
  background: white;
  color: #4a90e2;
  border: 2px solid #4a90e2;
}

.action-btn.secondary:hover {
  background: #4a90e2;
  color: white;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .section {
    padding: 20px;
  }
  
  .quick-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .action-btn {
    width: 200px;
    justify-content: center;
  }
}
</style>